// File: src/pages/CreateUserAccount.jsx
// Route example:
// <Route path="/app/accounts/create/:id" element={<CreateUserAccount />} />
// If :id = 0 => CREATE mode
// If :id != 0 => UPDATE mode

import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  CreateAccountSave,
  CreateAccountUpdate,
  _Get,
  _GetByUrlOnly,
  DeleteAccount,
} from "../import/ApiEndPoint/ApiCalling";
import { View_User_By_Id } from "../import/ApiEndPoint/Api";

const ACCOUNT_TYPES = [
  "Capital Account",
  "Reserves and Surplus",
  "Current Liabilities",
  "Sundry Creditors",
  "Duties and Taxes",
  "Provisions",
  "Bank OCC Account",
  "Bank OD Account",
  "Secured Loans",
  "Unsecured Loans",
  "Deposits (Liability)",
  "Fixed Assets",
  "Investments",
  "Current Assets",
  "Bank Accounts",
  "Cash-in-Hand",
  "Deposits (Asset)",
  "Loans and Advances (Asset)",
  "Stock-in-Hand",
  "Sundry Debtors",
  "Sales Account",
  "Purchase Account",
  "Direct Incomes",
  "Indirect Incomes",
  "Direct Expenses",
  "Indirect Expenses",
  "Branch/Divisions",
  "Misc. Expenses (Asset)",
];

const ACCOUNT_BULK_HEADERS = [
  "firstName",
  "account",
  "Type",
  "OpeningBalance",
  "openingFinancialYear",
];

// Accept both backend field names and the human-readable headings used by
// the Account List Excel export.
const ACCOUNT_BULK_HEADER_ALIASES = {
  firstName: [
    "firstName",
    "first name",
    "full name",
    "fullname",
    "name",
    "account name",
    "ledger name",
  ],
  account: ["account", "account type", "account group", "group"],
  Type: [
    "Type",
    "balance type",
    "bal type",
    "o/p balance type",
    "opening balance type",
    "dr/cr",
    "dr cr",
  ],
  OpeningBalance: [
    "OpeningBalance",
    "opening balance",
    "balance",
    "op balance",
    "o/p balance",
  ],
  openingFinancialYear: [
    "openingFinancialYear",
    "opening financial year",
    "financial year",
    "opening fy",
    "fy",
  ],
};

const ACCOUNT_SAMPLE_ROW = {
  firstName: "SAMPLE ACCOUNT - DELETE BEFORE UPLOAD",
  account: "Capital Account",
  Type: "debit",
  OpeningBalance: "0",
  openingFinancialYear: "2026-27",
};

const injectCssOnce = () => {
  const id = "create-user-account-css";
  if (document.getElementById(id)) return;

  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    .cua-shell { padding: 12px; }
    .cua-card {
      border-radius: 18px;
      border: 1px solid #e5e7eb;
      box-shadow: 0 10px 24px rgba(0,0,0,0.06);
      overflow: hidden;
      background: #fff;
    }

    .cua-head {
      padding: 14px 16px;
      background: linear-gradient(90deg, #fc8019, #f97316);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .cua-title {
      font-weight: 1000;
      letter-spacing: .02em;
      text-transform: uppercase;
      margin: 0;
      font-size: 16px;
    }

    .cua-sub {
      font-weight: 700;
      opacity: .9;
      font-size: 12px;
      margin: 0;
    }

    .cua-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .cua-btn {
      border-radius: 12px !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      letter-spacing: .02em;
      padding: 6px 10px !important;
      border: none !important;
    }

    .cua-body { padding: 14px; }

    .cua-grid {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(var(--cua-cols, 4), minmax(0, 1fr));
    }

    @media (max-width: 1200px) {
      .cua-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    }

    @media (max-width: 900px) {
      .cua-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }

    @media (max-width: 600px) {
      .cua-grid { grid-template-columns: repeat(1, minmax(0, 1fr)); }
    }

    .cua-tile {
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 12px;
      background: #fff;
      cursor: pointer;
      transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease;
      min-height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-weight: 1000;
      color: #0f172a;
    }

    .cua-tile:hover {
      transform: translateY(-1px);
      box-shadow: 0 10px 18px rgba(0,0,0,0.08);
      border-color: #fdba74;
    }

    .cua-tile.active {
      background: linear-gradient(90deg, #2563eb, #1d4ed8);
      border-color: #2563eb;
      color: #fff;
      box-shadow: 0 14px 26px rgba(37,99,235,0.25);
    }

    .cua-formbox,
    .cua-bulkbox {
      margin-top: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      background: #fff;
      padding: 12px;
    }

    .cua-bulkbox { background: #f8fafc; }

    .cua-formtitle {
      font-weight: 1000;
      text-transform: uppercase;
      letter-spacing: .02em;
      font-size: 13px;
      margin: 0 0 10px 0;
      color: #0f172a;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .cua-pill {
      font-size: 11px;
      font-weight: 1000;
      padding: 4px 10px;
      border-radius: 999px;
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
    }

    .cua-req { color: #ef4444; font-weight: 1000; }

    .cua-upload {
      border: 1px dashed #94a3b8;
      background: #fff;
      border-radius: 14px;
      padding: 18px;
      text-align: center;
    }

    .cua-note {
      margin-top: 8px;
      color: #b91c1c;
      font-size: 12px;
      font-weight: 900;
    }

    .cua-bulk-card {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      margin-top: 10px;
      box-shadow: 0 2px 8px rgba(0,0,0,.08);
      overflow: hidden;
    }

    .cua-bulk-card-head {
      min-height: 54px;
      padding: 10px 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      cursor: pointer;
      background: #fff;
    }

    .cua-bulk-card-title {
      font-size: 14px;
      font-weight: 1000;
      color: #0f172a;
    }

    .cua-bulk-card-sub {
      margin-top: 3px;
      font-size: 12px;
      color: #64748b;
      font-weight: 700;
    }

    .cua-bulk-card-error {
      margin-top: 5px;
      font-size: 12px;
      color: #b91c1c;
      font-weight: 800;
    }

    .cua-bulk-card-cross {
      border: none;
      background: transparent;
      font-size: 24px;
      line-height: 1;
      padding: 0 5px;
      cursor: pointer;
      color: #334155;
    }

    .cua-bulk-card-body {
      padding: 12px;
      border-top: 1px solid #e5e7eb;
      background: #fff;
    }
  `;

  document.head.appendChild(style);
};

/* ================= Financial Year Helpers ================= */

const normalizeOpeningFinancialYear = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);

  if (!match) return raw;

  const startYear = match[1];
  const endYear = match[2].length === 4 ? match[2].slice(-2) : match[2];
  return `${startYear}-${endYear}`;
};

const getFYStartYear = (date = new Date()) => {
  const currentDate = new Date(date);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  return month >= 3 ? year : year - 1;
};

const getCurrentOpeningFinancialYear = () => {
  const startYear = getFYStartYear(new Date());
  return normalizeOpeningFinancialYear(`${startYear}-${startYear + 1}`);
};

const buildOpeningFinancialYearOptions = (past = 5, future = 2) => {
  const currentStartYear = getFYStartYear(new Date());
  const options = [];

  for (
    let year = currentStartYear - past;
    year <= currentStartYear + future;
    year += 1
  ) {
    options.push(normalizeOpeningFinancialYear(`${year}-${year + 1}`));
  }

  return options.reverse();
};

const makeEmptyAccountForm = () => ({
  account: "",
  firstName: "",
  openingFinancialYear: getCurrentOpeningFinancialYear(),
  OpeningBalance: "",
  Type: "",
});

/* ================= Bulk helpers ================= */

const normalizeBulkKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getCanonicalAccountHeader = (value) => {
  const normalizedValue = normalizeBulkKey(value);
  if (!normalizedValue) return "";

  for (const [canonicalHeader, aliases] of Object.entries(
    ACCOUNT_BULK_HEADER_ALIASES,
  )) {
    const matched = aliases.some(
      (alias) => normalizeBulkKey(alias) === normalizedValue,
    );

    if (matched) return canonicalHeader;
  }

  return "";
};

const findAccountHeaderRowIndex = (matrix = []) => {
  let bestIndex = -1;
  let bestScore = 0;

  (Array.isArray(matrix) ? matrix : []).forEach((row, rowIndex) => {
    const canonicalHeaders = new Set(
      (Array.isArray(row) ? row : [])
        .map(getCanonicalAccountHeader)
        .filter(Boolean),
    );

    const hasRequiredHeaders =
      canonicalHeaders.has("firstName") && canonicalHeaders.has("account");

    if (!hasRequiredHeaders) return;

    const score = ACCOUNT_BULK_HEADERS.filter((header) =>
      canonicalHeaders.has(header),
    ).length;

    if (score > bestScore) {
      bestScore = score;
      bestIndex = rowIndex;
    }
  });

  return bestIndex;
};

const cleanBulkCell = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const getBulkCell = (row, aliases) => {
  const normalizedRow = {};

  Object.entries(row || {}).forEach(([key, value]) => {
    normalizedRow[normalizeBulkKey(key)] = value;
  });

  for (const alias of aliases) {
    const normalizedAlias = normalizeBulkKey(alias);

    if (Object.prototype.hasOwnProperty.call(normalizedRow, normalizedAlias)) {
      return normalizedRow[normalizedAlias];
    }
  }

  return "";
};

const resolveAccountType = (value) => {
  const raw = cleanBulkCell(value).toLowerCase();
  return (
    ACCOUNT_TYPES.find((accountType) => accountType.toLowerCase() === raw) || ""
  );
};

const normalizeBalanceType = (value) => {
  const raw = cleanBulkCell(value).toLowerCase();

  if (["debit", "dr", "d"].includes(raw)) return "debit";
  if (["credit", "cr", "c"].includes(raw)) return "credit";

  return raw;
};

const validateAccountData = (source = {}) => {
  const errors = [];
  const firstName = cleanBulkCell(source?.firstName);
  const account = cleanBulkCell(source?.account);
  const openingFinancialYear = normalizeOpeningFinancialYear(
    source?.openingFinancialYear,
  );
  const Type = normalizeBalanceType(source?.Type);

  if (!firstName) errors.push("Full Name is required.");

  if (!account) {
    errors.push("Account Type is required.");
  } else if (!ACCOUNT_TYPES.includes(account)) {
    errors.push("Please select one of the permitted account types.");
  }

  if (!openingFinancialYear) {
    errors.push("Opening Financial Year is required.");
  }

  if (Type && !["debit", "credit"].includes(Type)) {
    errors.push("O/P Balance Type must be Dr or Cr.");
  }

  if (
    source?.OpeningBalance !== "" &&
    source?.OpeningBalance !== null &&
    source?.OpeningBalance !== undefined &&
    !Number.isFinite(Number(source.OpeningBalance))
  ) {
    errors.push("Opening Balance must be a valid number.");
  }

  return errors;
};

const normalizeBulkAccountRow = (row, index) => {
  const firstName = cleanBulkCell(
    getBulkCell(row, ["firstName", "full name", "name"]),
  ).toUpperCase();

  const rawAccount = getBulkCell(row, ["account", "account type"]);
  const account = resolveAccountType(rawAccount);

  const Type = normalizeBalanceType(
    getBulkCell(row, ["Type", "balance type", "o/p balance type"]),
  );

  const openingBalanceRaw = cleanBulkCell(
    getBulkCell(row, ["OpeningBalance", "opening balance", "balance"]),
  );

  const openingFinancialYear = normalizeOpeningFinancialYear(
    getBulkCell(row, [
      "openingFinancialYear",
      "opening financial year",
      "financial year",
    ]) || getCurrentOpeningFinancialYear(),
  );

  const numericBalance =
    openingBalanceRaw === "" ? "" : Number(openingBalanceRaw);

  const data = {
    firstName,
    account,
    Type,
    OpeningBalance:
      openingBalanceRaw === "" || !Number.isFinite(numericBalance)
        ? openingBalanceRaw
        : numericBalance,
    openingFinancialYear,
  };

  const errors = validateAccountData(data);

  if (cleanBulkCell(rawAccount) && !account) {
    errors.push(
      `"${cleanBulkCell(rawAccount)}" is not an allowed Account Type.`,
    );
  }

  return {
    tempId: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    data,
    errorMessage: [...new Set(errors)].join(" "),
  };
};

const isEmptyBulkRow = (row) =>
  !Object.values(row || {}).some((value) => cleanBulkCell(value) !== "");

const readAccountRowsFromWorksheet = (worksheet) => {
  const matrix = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
  });

  // The exported Account List uses headings such as FULL NAME, TYPE,
  // BALANCE and OPENING FINANCIAL YEAR, whereas the downloadable bulk
  // template uses backend field names such as firstName and OpeningBalance.
  // Detect and canonicalize either format.
  const headerIndex = findAccountHeaderRowIndex(matrix);

  if (headerIndex < 0) {
    throw new Error(
      "Could not find the account upload headers. The worksheet must contain FULL NAME (or firstName) and ACCOUNT columns.",
    );
  }

  const headerRow = matrix[headerIndex].map((cell) => {
    return getCanonicalAccountHeader(cell) || cleanBulkCell(cell);
  });

  return matrix
    .slice(headerIndex + 1)
    .map((row) => {
      const objectRow = {};

      headerRow.forEach((header, columnIndex) => {
        if (!header) return;
        objectRow[header] = row?.[columnIndex] ?? "";
      });

      return objectRow;
    })
    .filter((row) => !isEmptyBulkRow(row))
    .filter((row) => {
      const firstName = cleanBulkCell(
        getBulkCell(row, ["firstName", "full name", "name"]),
      );
      return !/^sample account/i.test(firstName);
    });
};

const CreateUserAccount = () => {
  const navigate = useNavigate();
  const params = useParams();
  const isCreate = String(params?.id) === "0";

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(isCreate ? "Create" : "Update");

  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [expandedBulkIds, setExpandedBulkIds] = useState(() => new Set());
  const [bulkSavingId, setBulkSavingId] = useState("");
  const [bulkSaveAllLoading, setBulkSaveAllLoading] = useState(false);

  const openingFYOptions = useMemo(
    () => buildOpeningFinancialYearOptions(5, 2),
    [],
  );

  const [form, setForm] = useState(makeEmptyAccountForm);

  useEffect(() => injectCssOnce(), []);

  useEffect(() => {
    setMode(isCreate ? "Create" : "Update");

    if (!isCreate) {
      setBulkMode(false);
      setBulkRows([]);
      setBulkFileName("");
      setExpandedBulkIds(new Set());
      setBulkSavingId("");
      setBulkSaveAllLoading(false);
    }
  }, [isCreate]);

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  }, []);

  /* ================= Update mode loader ================= */

  useEffect(() => {
    if (isCreate) return;

    (async () => {
      setLoading(true);

      try {
        let response;

        try {
          response = await _GetByUrlOnly(
            `${String(View_User_By_Id).replace(/\/+$/, "")}/${params?.id}`,
          );
        } catch {
          try {
            response = await _Get(View_User_By_Id, params?.id);
          } catch {
            response = null;
          }
        }

        let data =
          response?.User ||
          response?.user ||
          response?.data?.User ||
          response?.data?.user ||
          response?.data ||
          response;

        if (Array.isArray(data)) data = data[0] || null;

        if (!data || typeof data !== "object") {
          swal("Not found", "Unable to load account details", "error");
          return;
        }

        setForm({
          account: data?.account || data?.Account || "",
          firstName: data?.firstName || "",
          openingFinancialYear: normalizeOpeningFinancialYear(
            data?.openingFinancialYear || getCurrentOpeningFinancialYear(),
          ),
          OpeningBalance:
            data?.OpeningBalance === 0
              ? "0"
              : String(data?.OpeningBalance || ""),
          Type: normalizeBalanceType(data?.Type || ""),
        });
      } catch {
        swal("Error", "Could not load details", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [isCreate, params?.id]);

  /* ================= Shared account helpers ================= */

  const buildPayload = (source = form) => {
    const database = userData?.database || "";
    const createdBy = userData?._id || userData?.id || "";
    const firstName = String(source?.firstName || "")
      .trim()
      .toUpperCase();
    const account = String(source?.account || "").trim();

    return {
      account,
      firstName,
      openingFinancialYear: normalizeOpeningFinancialYear(
        source?.openingFinancialYear || getCurrentOpeningFinancialYear(),
      ),
      OpeningBalance:
        source?.OpeningBalance === "" ||
        source?.OpeningBalance === null ||
        source?.OpeningBalance === undefined
          ? ""
          : Number(source.OpeningBalance),
      Type: normalizeBalanceType(source?.Type || ""),
      database,
      created_by: createdBy,
      id: `${firstName}-${account}`,
    };
  };

  const showValidationError = (source) => {
    const errors = validateAccountData(source);

    if (!errors.length) return true;

    swal("Invalid Account", errors.join("\n"), "warning");
    return false;
  };

  const onDelete = async () => {
    if (isCreate) return;

    const confirmation = await swal(
      "Warning",
      "Sure you want to delete this account?",
      {
        buttons: {
          cancel: "Cancel",
          catch: { text: "Delete", value: "delete" },
        },
      },
    );

    if (confirmation !== "delete") return;

    setLoading(true);

    try {
      await DeleteAccount(params?.id);
      swal("Deleted", "Deleted Successfully", "success");
      navigate(-1);
    } catch {
      swal("Error", "Delete failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= Normal form save ================= */

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!showValidationError(form)) return;

    setLoading(true);

    try {
      const payload = buildPayload(form);

      if (isCreate) {
        const response = await CreateAccountSave(payload);

        if (!response?.status) {
          throw new Error(response?.message || "Error in Saving User Account");
        }

        swal("Saved", "User Account Saved Successfully", "success");
        navigate(-1);
        return;
      }

      const response = await CreateAccountUpdate(params?.id, payload);

      if (!response?.status) {
        throw new Error(response?.message || "Error in Updating User Account");
      }

      swal("Updated", "User Account Updated Successfully", "success");
      navigate(-1);
    } catch (error) {
      swal(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================= Excel format ================= */

  const downloadBulkFormat = () => {
    const workbook = XLSX.utils.book_new();
    const headerRowNumber = ACCOUNT_TYPES.length + 6;

    const matrix = [
      ["ACCOUNT BULK UPLOAD FORMAT", "", "", "", ""],
      ["Strictly forbidden to use these account types only.", "", "", "", ""],
      [],
      ["ALLOWED ACCOUNT TYPES", "", "", "", ""],
      ...ACCOUNT_TYPES.map((accountType) => [accountType, "", "", "", ""]),
      [],
      ACCOUNT_BULK_HEADERS,
      ACCOUNT_BULK_HEADERS.map((header) => ACCOUNT_SAMPLE_ROW[header] || ""),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(matrix);
    worksheet["!cols"] = [
      { wch: 34 },
      { wch: 32 },
      { wch: 14 },
      { wch: 18 },
      { wch: 24 },
    ];
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
    ];
    worksheet["!autofilter"] = {
      ref: `A${headerRowNumber}:E${headerRowNumber + 1}`,
    };

    XLSX.utils.book_append_sheet(workbook, worksheet, "Account Bulk Upload");

    const fileBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([fileBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "Account_Bulk_Upload_Format.xlsx",
    );
  };

  /* ================= Bulk upload state ================= */

  const handleBulkFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setBulkFileName(file.name);

    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, {
        type: "array",
        raw: false,
      });

      const preferredSheetName =
        workbook.SheetNames.find((sheetName) => {
          const normalizedName = String(sheetName || "")
            .trim()
            .toLowerCase();

          return ["account bulk upload", "account list", "accounts"].includes(
            normalizedName,
          );
        }) || workbook.SheetNames[0];

      if (!preferredSheetName) {
        throw new Error("No worksheet found in this file.");
      }

      const worksheet = workbook.Sheets[preferredSheetName];
      const accountRows = readAccountRowsFromWorksheet(worksheet);

      if (!accountRows.length) {
        setBulkRows([]);
        setExpandedBulkIds(new Set());
        swal(
          "No Data",
          "No account rows were found below the upload headers.",
          "warning",
        );
        return;
      }

      const normalizedRows = accountRows.map(normalizeBulkAccountRow);

      // Same behaviour as Create User / Create Customer:
      // one Excel row fills the normal form directly.
      if (normalizedRows.length === 1) {
        const singleRow = normalizedRows[0];
        setForm({ ...singleRow.data });
        setBulkRows([]);
        setExpandedBulkIds(new Set());
        setBulkMode(false);

        swal(
          "File Loaded",
          singleRow.errorMessage
            ? `The account was filled in the form. Please correct: ${singleRow.errorMessage}`
            : "The account data has been filled in the normal form.",
          singleRow.errorMessage ? "warning" : "success",
        );
        return;
      }

      // Multiple rows become expandable cards, each containing the full form UI.
      setBulkRows(normalizedRows);
      setExpandedBulkIds(new Set([normalizedRows[0].tempId]));

      const invalidCount = normalizedRows.filter(
        (row) => row.errorMessage,
      ).length;

      swal(
        "File Loaded",
        `${normalizedRows.length} account row(s) added.${
          invalidCount ? ` ${invalidCount} row(s) need correction.` : ""
        }`,
        invalidCount ? "warning" : "success",
      );
    } catch (error) {
      console.error("[CreateUserAccount] bulk file error:", error);
      setBulkRows([]);
      setExpandedBulkIds(new Set());

      swal(
        "Error",
        error?.message || "Could not read the selected Excel file.",
        "error",
      );
    } finally {
      if (event?.target) event.target.value = "";
    }
  };

  const toggleBulkCard = (tempId) => {
    setExpandedBulkIds((previous) => {
      const next = new Set(previous);
      if (next.has(tempId)) next.delete(tempId);
      else next.add(tempId);
      return next;
    });
  };

  const updateBulkRowData = (tempId, updater) => {
    setBulkRows((previous) =>
      previous.map((row) => {
        if (row.tempId !== tempId) return row;

        const nextData =
          typeof updater === "function"
            ? updater(row.data)
            : { ...row.data, ...updater };

        return {
          ...row,
          data: nextData,
          errorMessage: validateAccountData(nextData).join(" "),
        };
      }),
    );
  };

  const removeBulkRow = (tempId, event) => {
    event?.stopPropagation?.();

    setBulkRows((previous) => previous.filter((row) => row.tempId !== tempId));
    setExpandedBulkIds((previous) => {
      const next = new Set(previous);
      next.delete(tempId);
      return next;
    });
  };

  const saveOneBulkAccount = async (tempId) => {
    const row = bulkRows.find((item) => item.tempId === tempId);
    if (!row || bulkSavingId || bulkSaveAllLoading) return;

    const errors = validateAccountData(row.data);

    if (errors.length) {
      setBulkRows((previous) =>
        previous.map((item) =>
          item.tempId === tempId
            ? { ...item, errorMessage: errors.join(" ") }
            : item,
        ),
      );
      swal("Invalid Account", errors.join("\n"), "warning");
      return;
    }

    setBulkSavingId(tempId);

    try {
      const response = await CreateAccountSave(buildPayload(row.data));

      if (!response?.status) {
        throw new Error(response?.message || "Account save failed");
      }

      removeBulkRow(tempId);
      swal("Saved", "Account saved successfully", "success");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Account save failed";

      setBulkRows((previous) =>
        previous.map((item) =>
          item.tempId === tempId ? { ...item, errorMessage: message } : item,
        ),
      );

      swal("Error", message, "error");
    } finally {
      setBulkSavingId("");
    }
  };

  const saveAllBulkAccounts = async () => {
    if (!isCreate || !bulkRows.length || bulkSaveAllLoading || bulkSavingId) {
      return;
    }

    const confirmed = await swal({
      title: "Save all accounts?",
      text: `${bulkRows.length} account(s) will be processed. Saved rows will be removed and failed rows will remain.`,
      icon: "warning",
      buttons: ["Cancel", "Save All"],
    });

    if (!confirmed) return;

    setBulkSaveAllLoading(true);

    let savedCount = 0;
    const failedRows = [];

    for (const row of bulkRows) {
      const validationErrors = validateAccountData(row.data);

      if (validationErrors.length) {
        failedRows.push({
          ...row,
          errorMessage: validationErrors.join(" "),
        });
        continue;
      }

      try {
        const response = await CreateAccountSave(buildPayload(row.data));

        if (!response?.status) {
          throw new Error(response?.message || "Account save failed");
        }

        savedCount += 1;
      } catch (error) {
        failedRows.push({
          ...row,
          errorMessage:
            error?.response?.data?.message ||
            error?.message ||
            "Account save failed",
        });
      }
    }

    setBulkRows(failedRows);
    setExpandedBulkIds(new Set(failedRows.map((row) => row.tempId)));
    setBulkSaveAllLoading(false);

    if (!failedRows.length) setBulkFileName("");

    swal(
      "Bulk Upload Completed",
      `Saved: ${savedCount}\nFailed: ${failedRows.length}`,
      failedRows.length ? "warning" : "success",
    );
  };

  /* ================= Shared full form UI ================= */

  const renderAccountTypeGrid = (source, onPick, disabled = false) => (
    <div className="cua-grid" style={{ ["--cua-cols"]: 6 }}>
      {ACCOUNT_TYPES.map((accountType) => (
        <div
          key={accountType}
          className={`cua-tile ${
            source?.account === accountType ? "active" : ""
          }`}
          onClick={() => !disabled && onPick(accountType)}
          onKeyDown={(event) => {
            if (!disabled && (event.key === "Enter" || event.key === " ")) {
              onPick(accountType);
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          style={disabled ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
        >
          {accountType}
        </div>
      ))}
    </div>
  );

  const renderAccountFields = ({
    source,
    onFieldChange,
    onSave,
    saveLabel,
    saving,
  }) => {
    if (!source?.account) {
      return (
        <div
          style={{
            marginTop: 12,
            textAlign: "center",
            color: "#64748b",
            fontWeight: 900,
          }}
        >
          Select an Account Type to open the form.
        </div>
      );
    }

    return (
      <div className="cua-formbox">
        <div className="cua-formtitle">
          Account Details
          <span className="cua-pill">{source.account}</span>
        </div>

        <Form onSubmit={onSave}>
          <Row>
            <Col lg="6" md="6" sm="12">
              <FormGroup>
                <Label>
                  Full Name <span className="cua-req">*</span>
                </Label>
                <Input
                  name="firstName"
                  placeholder="Enter Full Name"
                  value={source?.firstName || ""}
                  onChange={onFieldChange}
                  required
                  disabled={saving}
                />
              </FormGroup>
            </Col>

            <Col lg="3" md="6" sm="12">
              <FormGroup>
                <Label>
                  Opening Financial Year <span className="cua-req">*</span>
                </Label>
                <Input
                  name="openingFinancialYear"
                  type="select"
                  value={
                    source?.openingFinancialYear ||
                    getCurrentOpeningFinancialYear()
                  }
                  onChange={onFieldChange}
                  required
                  disabled={saving}
                >
                  {openingFYOptions.map((financialYear) => (
                    <option key={financialYear} value={financialYear}>
                      {financialYear}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            <Col lg="3" md="6" sm="12">
              <FormGroup>
                <Label>Opening Balance</Label>
                <Input
                  type="number"
                  name="OpeningBalance"
                  placeholder="0"
                  value={source?.OpeningBalance ?? ""}
                  onChange={onFieldChange}
                  disabled={saving}
                />
              </FormGroup>
            </Col>

            <Col lg="3" md="6" sm="12">
              <FormGroup>
                <Label>O/P Balance Type</Label>
                <Input
                  name="Type"
                  type="select"
                  value={source?.Type || ""}
                  onChange={onFieldChange}
                  disabled={saving}
                >
                  <option value="">-- select --</option>
                  <option value="debit">Dr</option>
                  <option value="credit">Cr</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-1">
            <Col className="d-flex justify-content-center">
              <Button
                className="cua-btn"
                color="primary"
                type="submit"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" /> &nbsp;Saving...
                  </>
                ) : (
                  saveLabel
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  const renderNormalAccountForm = () => (
    <>
      {renderAccountTypeGrid(
        form,
        (account) => setForm((previous) => ({ ...previous, account })),
        loading,
      )}

      {renderAccountFields({
        source: form,
        onFieldChange: (event) => {
          const { name, value } = event.target;
          setForm((previous) => ({
            ...previous,
            [name]:
              name === "openingFinancialYear"
                ? normalizeOpeningFinancialYear(value)
                : value,
          }));
        },
        onSave: onSubmit,
        saveLabel: mode,
        saving: loading,
      })}
    </>
  );

  const renderBulkUpload = () => (
    <div className="cua-bulkbox">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <div className="cua-formtitle mb-1">Bulk Upload Accounts</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            One row fills the normal form. Multiple rows create expandable full
            account forms.
          </div>
        </div>

        <Button
          type="button"
          className="cua-btn"
          color="secondary"
          onClick={downloadBulkFormat}
          disabled={bulkSaveAllLoading || !!bulkSavingId}
        >
          Download Excel Format
        </Button>
      </div>

      <div className="cua-note">
        Strictly forbidden to use these account types only.
      </div>

      <div className="cua-upload mt-3">
        <Label style={{ fontWeight: 900, display: "block" }}>
          Select Account Excel File
        </Label>
        <Input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleBulkFileChange}
          disabled={bulkSaveAllLoading || !!bulkSavingId}
        />

        {bulkFileName && (
          <div className="mt-2">
            <Badge color="primary">Selected: {bulkFileName}</Badge>
          </div>
        )}
      </div>

      {bulkRows.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <strong>Unsaved Accounts: {bulkRows.length}</strong>
            <Button
              type="button"
              className="cua-btn"
              color="primary"
              onClick={saveAllBulkAccounts}
              disabled={bulkSaveAllLoading || !!bulkSavingId}
            >
              {bulkSaveAllLoading ? (
                <>
                  <Spinner size="sm" /> &nbsp;Saving All...
                </>
              ) : (
                "Save All"
              )}
            </Button>
          </div>

          <div className="mt-2">
            {bulkRows.map((row, index) => {
              const isExpanded = expandedBulkIds.has(row.tempId);
              const isSaving = bulkSavingId === row.tempId;
              const data = row.data || makeEmptyAccountForm();

              return (
                <div className="cua-bulk-card" key={row.tempId}>
                  <div
                    className="cua-bulk-card-head"
                    onClick={() => toggleBulkCard(row.tempId)}
                  >
                    <div>
                      <div className="cua-bulk-card-title">
                        {data?.firstName || `Account Row ${index + 1}`} -{" "}
                        {data?.account || "Select Account Type"}
                      </div>
                      <div className="cua-bulk-card-sub">
                        {data?.Type || "No balance type"} | Balance:{" "}
                        {data?.OpeningBalance === "" ||
                        data?.OpeningBalance === undefined
                          ? "0"
                          : data.OpeningBalance}{" "}
                        | FY: {data?.openingFinancialYear || "-"}
                      </div>
                      {row.errorMessage && (
                        <div className="cua-bulk-card-error">
                          {row.errorMessage}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="cua-bulk-card-cross"
                      onClick={(event) => removeBulkRow(row.tempId, event)}
                      disabled={
                        bulkSaveAllLoading ||
                        isSaving ||
                        (!!bulkSavingId && !isSaving)
                      }
                      aria-label="Remove account"
                    >
                      ×
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="cua-bulk-card-body">
                      {renderAccountTypeGrid(
                        data,
                        (account) =>
                          updateBulkRowData(row.tempId, (previous) => ({
                            ...previous,
                            account,
                          })),
                        bulkSaveAllLoading || isSaving,
                      )}

                      {renderAccountFields({
                        source: data,
                        onFieldChange: (event) => {
                          const { name, value } = event.target;
                          updateBulkRowData(row.tempId, (previous) => ({
                            ...previous,
                            [name]:
                              name === "openingFinancialYear"
                                ? normalizeOpeningFinancialYear(value)
                                : value,
                          }));
                        },
                        onSave: (event) => {
                          event.preventDefault();
                          saveOneBulkAccount(row.tempId);
                        },
                        saveLabel: "Save This Account",
                        saving: bulkSaveAllLoading || isSaving,
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="cua-shell">
      <Card className="cua-card">
        <div className="cua-head">
          <div>
            <p className="cua-title">{mode} User Account</p>
          </div>

          <p className="cua-sub">Select Account Type (click on a title)</p>

          <div className="cua-actions">
            {/* Bulk upload is available only in Create mode. */}
            {isCreate && (
              <>
                <Button
                  type="button"
                  className="cua-btn"
                  color="primary"
                  onClick={() => setBulkMode((previous) => !previous)}
                  disabled={loading || bulkSaveAllLoading || !!bulkSavingId}
                >
                  {bulkMode ? "Normal Account" : "Bulk Upload"}
                </Button>

                <Button
                  type="button"
                  className="cua-btn"
                  color="light"
                  onClick={downloadBulkFormat}
                  disabled={loading || bulkSaveAllLoading || !!bulkSavingId}
                >
                  Excel Format
                </Button>
              </>
            )}

            {!isCreate && (
              <Button
                type="button"
                className="cua-btn"
                color="danger"
                onClick={onDelete}
                disabled={loading}
              >
                Delete
              </Button>
            )}

            <Button
              type="button"
              className="cua-btn"
              color="secondary"
              onClick={() => navigate(-1)}
              disabled={loading || bulkSaveAllLoading || !!bulkSavingId}
            >
              Back
            </Button>
          </div>
        </div>

        <CardBody className="cua-body">
          {isCreate && bulkMode
            ? renderBulkUpload()
            : renderNormalAccountForm()}
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateUserAccount;
